import ApolloClient from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { ApolloLink } from 'apollo-link';
import { defaultDataIdFromObject, InMemoryCache } from 'apollo-cache-inmemory';
import QueryClientContext from 'contexts/queryClientContext';
import { ToastContainer, toast } from 'react-toastify';
import { injectStyle } from 'react-toastify/dist/inject-style';
import { pipe, isEmpty, map, pathOr, find } from 'rambda';
import React from 'react';
import { withCookies } from 'react-cookie';
import {
  AUTH_TOKEN,
  USERNAME,
  USER_ID,
  INF_NOTIFICATIONS,
  cookiesSetting,
  LOGOUT_MESSAGE,
  API_URL_COOKIE
} from 'common/constants';
import { homePath } from 'WebModule/app/routerPaths';
import { compose } from 'recompose';
import { injectIntl } from 'react-intl';
import { getDefaultEnvApiUrl } from '../utils/apiUrl';
import { DEFAULT_LANGUAGE } from '../WebModule/i18n/params';
import { getLanguage } from '../utils/i18n';

injectStyle();

const ErrorMessagesTranslations = intl => [
  { fromBE: 'Signature has expired', onFE: 'Boli ste odhlásený kvôli expirácii tokenu' },
  { fromBE: 'Please, enter valid credentials', onFE: 'Nesprávne prihlasovacie údaje' },
  { fromBE: 'Unauthorized', onFE: 'Nepovolený prístup' },
  {
    fromBE: 'User with the same username already exists.',
    onFE: intl.formatMessage({ id: 'query-client.messages.duplicate-username' })
  },
  {
    fromBE: 'Karta už je priradená.',
    onFE: intl.formatMessage({ id: 'query-client.messages.card-already-assigned' })
  }
];

const formulateErrorMessage = (message, intl) => {
  const outputMessage = find(x => x.fromBE === message, ErrorMessagesTranslations(intl));
  return outputMessage ? outputMessage.onFE : message;
};

const logoutUser = cookies => {
  cookies.remove(AUTH_TOKEN);
  cookies.remove(USERNAME);
  cookies.remove(USER_ID);
  cookies.remove(INF_NOTIFICATIONS);
  cookies.remove(API_URL_COOKIE);
  window.location.href = homePath;
};

const prependProtocol = url => (url.startsWith('http') ? url : `http://${url}`);

const trimSlash = url => url.replace(/\/+$/g, '');

const normalizeUrl = pipe(
  prependProtocol,
  trimSlash
);

const getClient = (cookies, intl) => {
  const token = cookies.get(AUTH_TOKEN);
  const apiUrl = cookies.get(API_URL_COOKIE) || getDefaultEnvApiUrl() || '';
  const defaultLanguage = getLanguage(cookies) || DEFAULT_LANGUAGE;

  return new ApolloClient({
    link: ApolloLink.from([
      onError(({ networkError, graphQLErrors }) => {
        if (networkError) {
          if (networkError.statusCode === 401) {
            const errorMessage = pathOr('', 'response.statusText', networkError);
            cookies.set(LOGOUT_MESSAGE, formulateErrorMessage(errorMessage, intl), cookiesSetting);
            logoutUser(cookies);
          }
        }

        if (!isEmpty(graphQLErrors)) {
          map(er => {
            if (er.code === 'auth-error') {
              cookies.set(LOGOUT_MESSAGE, formulateErrorMessage(er.message, intl), cookiesSetting);
              logoutUser(cookies);
            }
          }, graphQLErrors);
        }

        if (networkError && !isEmpty(graphQLErrors)) {
          if (networkError.statusCode === 403) {
            map(er => {
              const errorMessage = formulateErrorMessage(er.message, intl);
              toast.error(`${errorMessage}`, {
                autoClose: 60000,
                position: 'top-center'
              });
            }, graphQLErrors);
          }
        }
      }),
      new HttpLink({
        uri: `${normalizeUrl(apiUrl)}/graphql/`,
        headers: {
          Authorization: token ? `JWT ${token}` : '',
          'Accept-Language': `${defaultLanguage}`
        }
      })
    ]),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network', // used by Query components
        errorPolicy: 'none'
      },
      query: {
        fetchPolicy: 'no-cache', // manual query (via client), cache-and-network is not supportd
        errorPolicy: 'none'
      },
      mutate: {
        errorPolicy: 'none'
      }
    },
    cache: new InMemoryCache({
      // TODO: pri upgrade na apollo 3 je dataIdFromObject deprecated. Treba asi radsej prejst na type policies:
      //  https://www.apollographql.com/docs/react/caching/cache-configuration/
      //
      // Teoreticky by toto mohol byt validny config pre nas problem (rovnake id niektorych itemGroups, ked mame itemGroups z roznych zdrojov):
      // typePolicies: {
      //   ExternalGroupType: {
      //     keyFields: ["id", "source", ["record"]],
      //   },
      // },
      dataIdFromObject(responseObject) {
        switch (responseObject.__typename) {
          case 'ExternalGroupType':
            return `ExternalGroupType:${responseObject.id}:${responseObject.source.record}`;
          default:
            return defaultDataIdFromObject(responseObject);
        }
      }
    })
  });
};

export default compose(
  injectIntl,
  withCookies
)(({ children, cookies, intl }) => {
  const token = cookies.get(AUTH_TOKEN);
  const apiUrl = cookies.get(API_URL_COOKIE) || getDefaultEnvApiUrl() || '';
  const defaultLanguage = getLanguage(cookies) || DEFAULT_LANGUAGE;

  // Recreating the client on every render (e.g. after an unrelated cookie write)
  // drops the Apollo cache and makes the whole UI reload its data.
  const client = React.useMemo(() => getClient(cookies, intl), [token, apiUrl, defaultLanguage]);

  return (
    <QueryClientContext.Provider value={client}>
      {children}
      <ToastContainer theme="colored" toastStyle={{ borderRadius: 0 }} autoClose={60000} />
    </QueryClientContext.Provider>
  );
});
